import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Search, Check } from "lucide-react";

export type Country = { name: string; iso: string; dial: string; flag: string };

/** ~45 common countries, UK first so it can be the sensible default. */
export const COUNTRIES: Country[] = [
  { name: "United Kingdom", iso: "GB", dial: "+44", flag: "🇬🇧" },
  { name: "Ireland", iso: "IE", dial: "+353", flag: "🇮🇪" },
  { name: "United States", iso: "US", dial: "+1", flag: "🇺🇸" },
  { name: "Canada", iso: "CA", dial: "+1", flag: "🇨🇦" },
  { name: "Australia", iso: "AU", dial: "+61", flag: "🇦🇺" },
  { name: "New Zealand", iso: "NZ", dial: "+64", flag: "🇳🇿" },
  { name: "France", iso: "FR", dial: "+33", flag: "🇫🇷" },
  { name: "Germany", iso: "DE", dial: "+49", flag: "🇩🇪" },
  { name: "Spain", iso: "ES", dial: "+34", flag: "🇪🇸" },
  { name: "Italy", iso: "IT", dial: "+39", flag: "🇮🇹" },
  { name: "Netherlands", iso: "NL", dial: "+31", flag: "🇳🇱" },
  { name: "Belgium", iso: "BE", dial: "+32", flag: "🇧🇪" },
  { name: "Portugal", iso: "PT", dial: "+351", flag: "🇵🇹" },
  { name: "Sweden", iso: "SE", dial: "+46", flag: "🇸🇪" },
  { name: "Norway", iso: "NO", dial: "+47", flag: "🇳🇴" },
  { name: "Denmark", iso: "DK", dial: "+45", flag: "🇩🇰" },
  { name: "Finland", iso: "FI", dial: "+358", flag: "🇫🇮" },
  { name: "Poland", iso: "PL", dial: "+48", flag: "🇵🇱" },
  { name: "Switzerland", iso: "CH", dial: "+41", flag: "🇨🇭" },
  { name: "Austria", iso: "AT", dial: "+43", flag: "🇦🇹" },
  { name: "India", iso: "IN", dial: "+91", flag: "🇮🇳" },
  { name: "Singapore", iso: "SG", dial: "+65", flag: "🇸🇬" },
  { name: "Hong Kong", iso: "HK", dial: "+852", flag: "🇭🇰" },
  { name: "United Arab Emirates", iso: "AE", dial: "+971", flag: "🇦🇪" },
  { name: "Saudi Arabia", iso: "SA", dial: "+966", flag: "🇸🇦" },
  { name: "South Africa", iso: "ZA", dial: "+27", flag: "🇿🇦" },
  { name: "Nigeria", iso: "NG", dial: "+234", flag: "🇳🇬" },
  { name: "Kenya", iso: "KE", dial: "+254", flag: "🇰🇪" },
  { name: "Brazil", iso: "BR", dial: "+55", flag: "🇧🇷" },
  { name: "Mexico", iso: "MX", dial: "+52", flag: "🇲🇽" },
  { name: "Argentina", iso: "AR", dial: "+54", flag: "🇦🇷" },
  { name: "Japan", iso: "JP", dial: "+81", flag: "🇯🇵" },
  { name: "South Korea", iso: "KR", dial: "+82", flag: "🇰🇷" },
  { name: "China", iso: "CN", dial: "+86", flag: "🇨🇳" },
  { name: "Philippines", iso: "PH", dial: "+63", flag: "🇵🇭" },
  { name: "Malaysia", iso: "MY", dial: "+60", flag: "🇲🇾" },
  { name: "Thailand", iso: "TH", dial: "+66", flag: "🇹🇭" },
  { name: "Indonesia", iso: "ID", dial: "+62", flag: "🇮🇩" },
  { name: "Turkey", iso: "TR", dial: "+90", flag: "🇹🇷" },
  { name: "Greece", iso: "GR", dial: "+30", flag: "🇬🇷" },
  { name: "Czechia", iso: "CZ", dial: "+420", flag: "🇨🇿" },
  { name: "Romania", iso: "RO", dial: "+40", flag: "🇷🇴" },
  { name: "Hungary", iso: "HU", dial: "+36", flag: "🇭🇺" },
  { name: "Ukraine", iso: "UA", dial: "+380", flag: "🇺🇦" },
  { name: "Pakistan", iso: "PK", dial: "+92", flag: "🇵🇰" },
];

export function CountrySelect({
  value,
  onChange,
}: {
  value: Country;
  onChange: (c: Country) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.dial.toLowerCase().includes(q) ||
        c.iso.toLowerCase().includes(q)
    );
  }, [query]);

  // Reset the query and autofocus the search each time the panel opens.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    const t = setTimeout(() => searchRef.current?.focus(), 40);
    return () => clearTimeout(t);
  }, [open]);

  // Close on outside click (ignore clicks inside the root) and Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Capture-phase + stopImmediatePropagation so Escape closes only this
        // dropdown, never the surrounding modal (which also listens on document).
        e.stopPropagation();
        e.stopImmediatePropagation();
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  function select(c: Country) {
    onChange(c);
    setOpen(false);
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Country code: ${value.name} ${value.dial}`}
        className="flex cursor-pointer items-center gap-1.5 rounded-2xl border-[3px] border-navy bg-white px-3 py-3 text-navy outline-none transition focus:ring-2 focus:ring-teal focus:ring-offset-2 focus:ring-offset-cream"
      >
        <span className="text-lg leading-none" aria-hidden="true">
          {value.flag}
        </span>
        <span className="num text-base font-bold">{value.dial}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={3}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -4 }}
            transition={{ duration: 0.14 }}
            className="absolute left-0 top-full z-20 mt-2 w-64 max-w-[80vw] overflow-hidden rounded-2xl border-[3px] border-navy bg-white shadow-pop"
            role="listbox"
          >
            <div className="border-b-[3px] border-navy bg-cream p-2">
              <div className="flex items-center gap-2 rounded-xl border-2 border-navy bg-white px-3 py-2">
                <Search className="h-4 w-4 shrink-0 text-charcoal/50" strokeWidth={3} />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (filtered[0]) select(filtered[0]);
                    }
                  }}
                  placeholder="Search country or code"
                  className="w-full bg-transparent text-base text-navy placeholder-charcoal/40 outline-none"
                />
              </div>
            </div>

            <div className="max-h-60 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <p className="px-4 py-3 text-sm text-charcoal/60">No matches.</p>
              ) : (
                filtered.map((c) => {
                  const selected = c.iso === value.iso && c.dial === value.dial;
                  return (
                    <button
                      key={`${c.iso}-${c.dial}`}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => select(c)}
                      className={`flex w-full cursor-pointer items-center gap-2.5 px-4 py-2.5 text-left text-base transition hover:bg-teal-mist ${
                        selected ? "bg-teal-mist font-bold text-navy" : "text-navy"
                      }`}
                    >
                      <span className="text-lg leading-none" aria-hidden="true">
                        {c.flag}
                      </span>
                      <span className="flex-1 truncate">{c.name}</span>
                      <span className="num text-sm text-charcoal/70">{c.dial}</span>
                      {selected && (
                        <Check className="h-4 w-4 shrink-0 text-teal" strokeWidth={3} />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
